import {TNN_PERCENTAGE_DEFAULT} from '../../../../config/commonConfig';
import type {ICompany} from '../../../../dto/Company.dto';
import type {IDistrictChildFields} from './districtPartners';

export type TnnPartnerProductKey = 'gametime' | 'neptuneRadio';

export const TNN_PARTNER_AMOUNT_FIELDS = {
  price: 'share_price',
  share: 'dollar_value',
} as const;

export type TnnPartnerAmountField =
  (typeof TNN_PARTNER_AMOUNT_FIELDS)[keyof typeof TNN_PARTNER_AMOUNT_FIELDS];

export type TnnPartnerAmountValue = number | string | null | undefined;

export type TnnPartnerAmountOptions = {
  preferPivot?: boolean;
  calculateShareWhenMissing?: boolean;
};

export type ITnnPartnerAmountPartner = IDistrictChildFields & {
  id: number | string;
  name?: string | null;
  partner_name?: string | null;
  share_price?: TnnPartnerAmountValue;
  dollar_value?: TnnPartnerAmountValue;
  total_events?: number | null;
  runs_to_date?: number | null;
  pivot?: {
    share_price?: TnnPartnerAmountValue;
    dollar_value?: TnnPartnerAmountValue;
  } | null;
};

export type IPartnerCompany = ICompany &
  Omit<ITnnPartnerAmountPartner, 'id' | 'name'> & {
    name: string;
  };

export type TnnPartnerProductConfig = {
  key: TnnPartnerProductKey;
  label: string;
  backendProductValue: 'GameTime' | 'Neptune Radio';
};

export const TNN_PARTNER_PRODUCTS: Record<
  TnnPartnerProductKey,
  TnnPartnerProductConfig
> = {
  gametime: {
    key: 'gametime',
    label: 'GameTime',
    backendProductValue: 'GameTime',
  },
  neptuneRadio: {
    key: 'neptuneRadio',
    label: 'Neptune Radio',
    backendProductValue: 'Neptune Radio',
  },
};

const roundCurrency = (value: number) =>
  Math.round((value + Number.EPSILON) * 100) / 100;

export const sanitizePartnerPrice = (value: unknown): number => {
  if (typeof value === 'number') {
    return Number.isFinite(value) && value >= 0 ? roundCurrency(value) : 0;
  }

  const normalized = String(value ?? '')
    .trim()
    .replace(/[$,]/g, '');
  if (!normalized) return 0;

  const parsed = Number(normalized);
  return Number.isFinite(parsed) && parsed >= 0 ? roundCurrency(parsed) : 0;
};

const toTnnNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const getTnnPartnerAmount = (
  partner: ITnnPartnerAmountPartner,
  field: TnnPartnerAmountField,
  {preferPivot = false}: TnnPartnerAmountOptions = {},
): TnnPartnerAmountValue =>
  preferPivot
    ? partner.pivot?.[field] ?? partner[field]
    : partner[field] ?? partner.pivot?.[field];

export const getPartnerName = (partner: ITnnPartnerAmountPartner): string =>
  partner.name ?? partner.partner_name ?? '';

export const calculatePartnerShare = (partnerPrice: unknown): number =>
  roundCurrency(
    sanitizePartnerPrice(partnerPrice) * (TNN_PERCENTAGE_DEFAULT / 100),
  );

export const getPartnerPrice = (
  company: ITnnPartnerAmountPartner,
  options?: TnnPartnerAmountOptions,
): TnnPartnerAmountValue =>
  getTnnPartnerAmount(company, TNN_PARTNER_AMOUNT_FIELDS.price, options);

export const getPartnerShare = (
  company: ITnnPartnerAmountPartner,
  partnerPrice?: unknown,
  options: TnnPartnerAmountOptions = {},
): number => {
  const persistedShare = getTnnPartnerAmount(
    company,
    TNN_PARTNER_AMOUNT_FIELDS.share,
    options,
  );
  if (persistedShare != null) return sanitizePartnerPrice(persistedShare);
  if (options.calculateShareWhenMissing === false) return 0;

  return calculatePartnerShare(
    partnerPrice === undefined
      ? getTnnPartnerAmount(company, TNN_PARTNER_AMOUNT_FIELDS.price, options)
      : partnerPrice,
  );
};

export const sumPartnerAmount = (
  partners: ITnnPartnerAmountPartner[],
  field: TnnPartnerAmountField,
  options?: TnnPartnerAmountOptions,
): number | null => {
  const values = partners
    .map(partner => {
      if (
        field === TNN_PARTNER_AMOUNT_FIELDS.share &&
        options?.calculateShareWhenMissing
      ) {
        return toTnnNumber(getPartnerShare(partner, undefined, options));
      }

      const amount = getTnnPartnerAmount(partner, field, options);
      return field === TNN_PARTNER_AMOUNT_FIELDS.price
        ? sanitizePartnerPrice(amount)
        : toTnnNumber(amount);
    })
    .filter((value): value is number => value !== null);

  return values.length ? values.reduce((sum, value) => sum + value, 0) : null;
};

export const sumPartnerShare = (
  partners: ITnnPartnerAmountPartner[],
  options: TnnPartnerAmountOptions = {calculateShareWhenMissing: true},
): number | null =>
  sumPartnerAmount(
    partners,
    TNN_PARTNER_AMOUNT_FIELDS.share,
    options.calculateShareWhenMissing === undefined
      ? {...options, calculateShareWhenMissing: true}
      : options,
  );

export const formatTnnCurrency = (
  value: unknown,
  emptyValue = '—',
  minimumFractionDigits = 0,
): string => {
  const numberValue = toTnnNumber(value);
  if (numberValue === null) return emptyValue;

  return `$ ${numberValue.toLocaleString('en-US', {
    minimumFractionDigits,
    maximumFractionDigits: 2,
  })}`;
};

export const formatPartnerCurrency = (value: unknown): string =>
  formatTnnCurrency(value, '$ 0.00', 2);

export const normalizePartnerCompany = (
  company: ICompany | IPartnerCompany,
): IPartnerCompany => {
  const partner = company as IPartnerCompany;
  const sharePrice = getPartnerPrice(partner);
  return {
    ...partner,
    [TNN_PARTNER_AMOUNT_FIELDS.price]: sanitizePartnerPrice(sharePrice),
    [TNN_PARTNER_AMOUNT_FIELDS.share]: getPartnerShare(partner, sharePrice),
  };
};

export const normalizeProgressPartnerCompany = (company: {
  id: number | string;
  name?: string | null;
  share_price: number | string | null;
  dollar_value: number | string | null;
}): IPartnerCompany => ({
  id: Number(company.id),
  name: company.name ?? '',
  [TNN_PARTNER_AMOUNT_FIELDS.price]: sanitizePartnerPrice(company.share_price),
  [TNN_PARTNER_AMOUNT_FIELDS.share]:
    company.dollar_value == null
      ? calculatePartnerShare(company.share_price)
      : sanitizePartnerPrice(company.dollar_value),
});

export const updatePartnerCompanyPrice = (
  company: IPartnerCompany,
  rawValue: string,
): IPartnerCompany => ({
  ...company,
  // Keep the raw input string so controlled number fields do not rewrite the
  // value or lose the caret while the user is typing.
  [TNN_PARTNER_AMOUNT_FIELDS.price]: rawValue,
  [TNN_PARTNER_AMOUNT_FIELDS.share]: calculatePartnerShare(rawValue),
});

export const appendTnnPartnerFormData = (
  formData: FormData,
  fieldName: 'gameTime' | 'neptuneRadio',
  companies: IPartnerCompany[],
) => {
  companies.forEach((company, index) => {
    const partnerPrice = sanitizePartnerPrice(getPartnerPrice(company));
    formData.append(
      `${fieldName}[companies][${index}][id]`,
      String(company.id),
    );
    formData.append(
      `${fieldName}[companies][${index}][${TNN_PARTNER_AMOUNT_FIELDS.price}]`,
      String(partnerPrice),
    );
    formData.append(
      `${fieldName}[companies][${index}][${TNN_PARTNER_AMOUNT_FIELDS.share}]`,
      String(getPartnerShare(company, partnerPrice)),
    );
  });
};
